import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import Modal from '../common/Modal';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useUsdtBalance } from '../../hooks/useUsdtBalance';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt } from '../../utils/format';
import { CONTRACTS, PACKAGE_PRICES, TOTAL_PACKAGES } from '../../config/contracts';

// Accepts either a wallet address (0x...) OR a numeric user ID.
// Looks up the target, auto-picks their next tier, then approve+activate in one flow.
const ActivateForModal = ({ initialWallet = '', onClose, onSuccess }) => {
  const { logic, usdt, storage } = useContracts();
  const { balance, allowance, refresh: refreshBalance } = useUsdtBalance();
  const notify = useNotify();

  const [query, setQuery] = useState(initialWallet || '');
  const [pkgId, setPkgId] = useState(1);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [targetInfo, setTargetInfo] = useState(null); // { id, name, wallet, topPkg }
  const [lookingUp, setLookingUp] = useState(false);
  const [inputMode, setInputMode] = useState('auto'); // just informational — auto-detected

  // Auto-lookup: detect whether input is address or user ID, then fetch details.
  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      const q = query.trim();
      if (!q) { setTargetInfo(null); setInputMode('auto'); return; }

      setLookingUp(true);
      try {
        let id;
        let walletAddr;

        if (ethers.utils.isAddress(q)) {
          // Wallet address input
          setInputMode('wallet');
          walletAddr = ethers.utils.getAddress(q);
          const uid = await storage.addressToId(walletAddr);
          id = uid.toNumber ? uid.toNumber() : Number(uid);
        } else if (/^\d+$/.test(q)) {
          // Numeric user ID input
          setInputMode('id');
          id = parseInt(q, 10);
        } else {
          setInputMode('auto');
          setTargetInfo(null);
          setLookingUp(false);
          return;
        }

        if (!id) {
          if (!cancelled) setTargetInfo({ notFound: true });
          setLookingUp(false);
          return;
        }

        const u = await storage.users(id);
        if (cancelled) return;
        const numId = u[1].toNumber ? u[1].toNumber() : Number(u[1]);
        if (numId === 0) {
          setTargetInfo({ notFound: true });
        } else {
          const topPkg = u[8].toNumber ? u[8].toNumber() : Number(u[8]);
          setTargetInfo({
            id: numId,
            wallet: u[0],
            topPkg,
            isBlocked: u[5]
          });
          setPkgId(Math.min(topPkg + 1, TOTAL_PACKAGES));
        }
      } catch (_) {
        if (!cancelled) setTargetInfo({ notFound: true });
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    };
    const t = setTimeout(lookup, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, storage]);

  const price = PACKAGE_PRICES[pkgId - 1];
  const priceBn = ethers.utils.parseUnits(String(price), 18);
  const needsApproval = allowance.lt(priceBn);
  const insufficientBalance = balance.lt(priceBn);
  const canActivate = targetInfo && !targetInfo.notFound && !targetInfo.isBlocked;

  const activate = async () => {
    if (!canActivate) { notify.error('Enter a valid wallet or user ID'); return; }

    setBusy(true);
    try {
      if (needsApproval) {
        setStep('approving');
        const l = notify.loading('Approving USDT (one-time)...');
        try {
          const tx = await usdt.approve(CONTRACTS.GlobalStorage, ethers.constants.MaxUint256);
          notify.dismiss(l);
          const m = notify.loading('Confirming approval...');
          await tx.wait();
          notify.dismiss(m);
          await refreshBalance();
        } catch (err) {
          notify.dismiss(l);
          notify.error(parseError(err));
          setBusy(false); setStep('');
          return;
        }
      }

      setStep('activating');
      const l = notify.loading(`Activating Package ${pkgId} for ${targetInfo.id}...`);
      try {
        // Contract only has activatePackageFor(address, uint256) — use the wallet from lookup
        const tx = await logic.activatePackageFor(targetInfo.wallet, pkgId);
        notify.dismiss(l);
        const m = notify.loading('Confirming...');
        await tx.wait();
        notify.dismiss(m);
        notify.success(`Package ${pkgId} activated for ${targetInfo.id}`);
        if (onSuccess) onSuccess();
        onClose();
      } catch (err) {
        notify.dismiss(l);
        notify.error(parseError(err));
      }
    } finally {
      setBusy(false);
      setStep('');
    }
  };

  return (
    <Modal isOpen={true} onClose={busy ? undefined : onClose} title="Activate Package for User">
      <div className="input-group">
        <label className="input-label">
          Target
          <span className="badge chip-muted" style={{ marginLeft: 8, fontSize: 9 }}>
            {inputMode === 'wallet' ? 'Wallet detected' : inputMode === 'id' ? 'User ID detected' : 'Wallet or User ID'}
          </span>
        </label>
        <input
          className="input font-mono"
          placeholder="0x... or User ID (e.g. 1234)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {lookingUp && (
          <span className="input-hint"><MiniLoader /> Looking up...</span>
        )}

        {!lookingUp && targetInfo && !targetInfo.notFound && (
          <div className="upline-preview" style={{ marginTop: 8 }}>
            <div className="upline-avatar">
              {String(targetInfo.id).charAt(0)}
            </div>
            <div className="upline-info">
              <div className="upline-name">
                User #{targetInfo.id}
                {targetInfo.isBlocked && <span className="badge badge-error" style={{ marginLeft: 6, fontSize: 9 }}>Blocked</span>}
              </div>
              <div className="upline-meta">
                Current top: {targetInfo.topPkg > 0 ? `P${targetInfo.topPkg}` : 'None'}
              </div>
            </div>
            {!targetInfo.isBlocked && <div className="upline-badge">✓ Found</div>}
          </div>
        )}

        {!lookingUp && targetInfo?.notFound && (
          <span className="input-error">✕ User not found</span>
        )}
      </div>

      <div className="input-group">
        <label className="input-label">
          Package
          {targetInfo && !targetInfo.notFound && targetInfo.topPkg > 0 && pkgId === targetInfo.topPkg + 1 && (
            <span className="badge badge-aurora" style={{ marginLeft: 8, fontSize: 9 }}>Next tier</span>
          )}
        </label>
        <select className="input" value={pkgId} onChange={(e) => setPkgId(parseInt(e.target.value, 10))}>
          {Array.from({ length: TOTAL_PACKAGES }, (_, i) => i + 1).map((p) => (
            <option key={p} value={p}>Package {p} — ${PACKAGE_PRICES[p - 1]}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div className="flex flex-between" style={{ fontSize: 13 }}>
          <span className="text-muted">Your USDT Balance</span>
          <span className={insufficientBalance ? 'text-error' : ''}>{formatUsdt(balance)} USDT</span>
        </div>
      </div>

      <div className="flex gap-sm">
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button
          className="btn btn-primary btn-lg flex-1"
          onClick={activate}
          disabled={busy || insufficientBalance || !canActivate}
          style={{ fontSize: 15, padding: '16px 22px' }}
        >
          {busy ? (
            <>
              <MiniLoader />
              {step === 'approving' ? 'Approving...' : step === 'activating' ? 'Activating...' : 'Processing...'}
            </>
          ) : (
            <>⚡ Pay ${price} & Activate</>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ActivateForModal;
